import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Check, CheckCheck, Mail, CalendarClock, BellRing, Wrench } from "lucide-react";
import { WorkOrderFormModal } from "../components/workorders/WorkOrderFormModal";
import { CronStatusCard } from "../components/shared/CronStatusCard";
import { EscalationBadge } from "../components/shared/EscalationBadge";
import { api, errorMessage } from "../lib/api";
import type { Alert } from "../lib/types";
import { useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/utils";
import { PageHeader, FilterChip, Card } from "../components/core/Layout";
import { Button } from "../components/core/Button";
import { Plate, StatusBadge } from "../components/core/Badges";
import { TenantSelect } from "../components/shared/TenantSelect";

interface Notif { id: string; to: string; subject: string; channel: string; status: string; created_at: string }

export default function AlertsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isBO = user?.user_type === "super_admin";
  const base = isBO ? "/bo" : "/app";
  const [params] = useSearchParams();
  const fromParam = (v: string | null) => (v === "all" ? "" : v || "open");
  const [tenantId, setTenantId] = useState("");
  const [status, setStatus] = useState(fromParam(params.get("status")));
  useEffect(() => { setStatus(fromParam(params.get("status"))); }, [params]);
  const [severity, setSeverity] = useState("");
  const [scanning, setScanning] = useState(false);
  const [raise, setRaise] = useState<Alert | null>(null);

  const { data, isLoading } = useQuery<Alert[]>({ queryKey: ["alerts", tenantId, status, severity], queryFn: async () => (await api.get("/alerts", { params: { tenant_id: tenantId || undefined, status: status || undefined, severity: severity || undefined } })).data });
  const { data: notifs } = useQuery<{ email_mode: string; items: Notif[] }>({ queryKey: ["notifications", tenantId], queryFn: async () => (await api.get("/alerts/notifications", { params: { tenant_id: tenantId || undefined, limit: 15 } })).data });

  const update = async (a: Alert, s: "acknowledged" | "resolved") => {
    try {
      await api.put(`/alerts/${a.id}`, { status: s });
      toast.success(`Alert ${s}`);
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const scan = async () => {
    setScanning(true);
    try {
      const { data: r } = await api.post("/alerts/scan", null, { params: { tenant_id: tenantId || undefined } });
      toast.success(`${r.created} new alert(s) · ${r.reminders} reminder(s) sent`);
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setScanning(false);
    }
  };

  return (
    <div data-testid="alerts-page">
      <PageHeader eyebrow={isBO ? "Cross-client monitoring" : "Notifications"} title="Alert Center" subtitle="Compliance deadlines and operational exceptions. Alerts notify; they never block operations."
        actions={<>{isBO && <TenantSelect value={tenantId} onChange={setTenantId} className="w-52" />}<Button variant="secondary" onClick={scan} loading={scanning} data-testid="alerts-scan-button"><RefreshCw className="h-3.5 w-3.5" />Scan now</Button></>} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {[["open", "Open"], ["acknowledged", "Acknowledged"], ["resolved", "Resolved"], ["", "All"]].map(([s, l]) => <FilterChip key={s} active={status === s} onClick={() => setStatus(s)} testId={`alert-status-${s || "all"}`}>{l}</FilterChip>)}
        <span className="mx-1 h-5 w-px bg-slate-300" />
        {[["", "Any severity"], ["expired", "Expired"], ["critical", "Critical"], ["warning", "Warning"], ["action", "Action"]].map(([s, l]) => <FilterChip key={s} active={severity === s} onClick={() => setSeverity(s)} testId={`alert-severity-${s || "any"}`}>{l}</FilterChip>)}
      </div>
      <div className="grid gap-5 lg:grid-cols-12">
        <div className="space-y-2 lg:col-span-8" data-testid="alerts-list">
          {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
          {data && data.length === 0 && <div className="card-crisp py-12 text-center text-sm text-slate-500">No alerts in this view.</div>}
          {(data || []).map((a) => (
            <article key={a.id} className="card-crisp flex flex-wrap items-start justify-between gap-3 p-3.5 rise" data-testid={`alert-card-${a.id}`}>
              <div className="flex min-w-0 items-start gap-3">
                <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${a.severity === "warning" ? "bg-[color:var(--gold)]" : a.severity === "critical" ? "bg-orange-500" : "bg-rose-600"}`} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2"><StatusBadge status={a.severity} /><StatusBadge status={a.status} testId={`alert-status-badge-${a.id}`} />{isBO && a.tenant_name && <span className="text-[11px] font-semibold text-slate-500">{a.tenant_name}</span>}<EscalationBadge a={a} /></div>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{a.title}</p>
                  <p className="text-xs text-slate-600">{a.message}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{fmtDateTime(a.created_at)}{a.acknowledged_by ? ` · ack by ${a.acknowledged_by}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {a.vehicle_id && <Link to={`${base}/vehicles/${a.vehicle_id}`} data-testid={`alert-vehicle-link-${a.id}`}><Plate plate={a.plate_number} /></Link>}
                {!isBO && a.type === "SERVICE_DUE" && a.status !== "resolved" && a.vehicle_id && <Button size="sm" onClick={() => setRaise(a)} data-testid={`raise-service-${a.id}`}><Wrench className="h-3.5 w-3.5" />Raise request</Button>}
                {a.status === "open" && <Button size="sm" variant="secondary" onClick={() => update(a, "acknowledged")} data-testid={`ack-alert-${a.id}`}><Check className="h-3.5 w-3.5" />Ack</Button>}
                {a.status !== "resolved" && <Button size="sm" variant="ghost" onClick={() => update(a, "resolved")} data-testid={`resolve-alert-${a.id}`}><CheckCheck className="h-3.5 w-3.5" />Resolve</Button>}
              </div>
            </article>
          ))}
        </div>
        <div className="space-y-5 lg:col-span-4">
          <CronStatusCard job="compliance-scan" title="Daily compliance scan" icon={CalendarClock} />
          <CronStatusCard job="approval-reminders" title="Approval reminders (48h)" icon={BellRing} />
          <Card title="Email notification log" testId="notifications-card">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] text-slate-500"><Mail className="h-3 w-3" />Sender: POMEN centralised · Mode: <b className="uppercase">{notifs?.email_mode === "mock" ? "MOCK (log only, add RESEND_API_KEY)" : "Resend live"}</b></p>
            <ul className="divide-y divide-slate-100">
              {(notifs?.items || []).map((n) => <li key={n.id} className="py-2 text-xs"><p className="font-semibold text-slate-800">{n.subject}</p><p className="text-slate-500">→ {n.to} · {n.channel}/{n.status} · {fmtDateTime(n.created_at)}</p></li>)}
              {notifs && notifs.items.length === 0 && <li className="py-2 text-xs text-slate-500">No emails sent yet. Run a scan.</li>}
            </ul>
          </Card>
        </div>
      </div>
      {raise && <WorkOrderFormModal open onClose={() => setRaise(null)} vehicleId={raise.vehicle_id || undefined} prefill={{ service_types: ["service"], title: raise.title.replace(/—.*/, `— ${raise.plate_number || ""}`).trim(), description: raise.message, odometer_km: raise.service?.current_km != null ? String(raise.service.current_km) : "" }} />}
    </div>
  );
}
